/**
 * Campus Data POJO Classes
 * Generated from data.json structure
 */

import { convertOperationalHoursToDisplay } from "@ciq/shared/utils/date/manager"
import { ZoneType } from "@ciq/shared/utils/const"
import { isEmptyArray } from "@ciq/shared/utils/array"
import { buildIdLookup } from "@ciq/shared/utils/object"

/**
 * Represents a campus building with its basic information and operating hours.
 *
 * @property {string} id - Unique identifier for the building
 * @property {string} name - Building name and number
 * @property {Array<Object>|null} operatingHours - Display-formatted operating hours with labels and structured data
 */
export class Building {
  /**
   * @param {Object} data - The building data
   * @param {string} data.id - Unique identifier for the building
   * @param {string} data.name - Building name and number
   * @param {Array} data.operating_hours - Array of operating hours by day
   */
  constructor(data) {
    this.id = data.id
    this.name = data.name
    this.centroid = data.centroid || null

    let operatingHours = null
    if (data.operating_hours?.length) {
      operatingHours = convertOperationalHoursToDisplay(data.operating_hours)
    }
    this.operatingHours = operatingHours
  }

  /**
   * Creates a Building instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {Building} New Building instance
   */
  static fromJson(json) {
    return new Building(json)
  }
}

/**
 * Represents a space usage type classification.
 *
 * @property {string} id - Unique identifier for the usage type
 * @property {string} code - Usage code (e.g., "100", "110")
 * @property {string} name - Usage name/description
 */
export class Usage {
  /**
   * @param {Object} data - The usage data
   * @param {string} data.id - Unique identifier for the usage type
   * @param {string} data.code - Usage code (e.g., "100", "110")
   * @param {string} data.name - Usage name/description
   */
  constructor(data) {
    this.id = data.id
    this.code = data.code
    this.name = data.name
  }

  /**
   * Creates a Usage instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {Usage} New Usage instance
   */
  static fromJson(json) {
    return new Usage(json)
  }
}

/**
 * Represents layout configuration for a zone.
 *
 * @property {string} iconColor - Icon color in CSS format
 */
export class ZoneLayout {
  /**
   * @param {Object} data - The layout data
   * @param {string} data.icon_color - Icon color in CSS format
   */
  constructor(data) {
    this.iconColor = data.icon_color
  }

  /**
   * Creates a ZoneLayout instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {ZoneLayout} New ZoneLayout instance
   */
  static fromJson(json) {
    return new ZoneLayout(json)
  }
}

/**
 * Represents a campus zone (building, floor, or room).
 *
 * @property {string} id - Unique identifier for the zone
 * @property {string} name - Zone name
 * @property {string} buildingId - Associated building ID
 * @property {string|null} parentId - Parent zone ID
 * @property {string} type - Type of zone (Building, Floor, etc.)
 * @property {number} capacity - Maximum capacity
 * @property {number} squareFootage - Square footage
 * @property {Array<string>} floors - Floor identifiers
 * @property {boolean} hasGeojson - Whether zone has geojson data
 * @property {boolean} isRoot - Whether this is a root zone
 * @property {ZoneLayout|undefined} layout - Layout configuration
 * @property {string|null} primaryUsageId - Primary usage type ID
 * @property {string|null} secondaryUsageId - Secondary usage type ID
 * @property {Array} rooms - Associated rooms
 * @property {Array<string>} tags - Zone tags
 */
export class Zone {
  /**
   * @param {Object} data - The zone data
   * @param {string} data.id - Unique identifier for the zone
   * @param {string} data.name - Zone name
   * @param {string} data.building_id - Associated building ID
   * @param {string|null} data.parent_id - Parent zone ID
   * @param {string} data.zone_type - Type of zone (Building, Floor, etc.)
   * @param {number} data.capacity - Maximum capacity
   * @param {number} data.square_footage - Square footage
   * @param {Array<string>} data.floors - Floor identifiers
   * @param {boolean} data.has_geojson - Whether zone has geojson data
   * @param {boolean} data.is_root - Whether this is a root zone
   * @param {Object} data.layout - Layout configuration
   * @param {string|null} data.primary_usage_id - Primary usage type ID
   * @param {string|null} data.secondary_usage_id - Secondary usage type ID
   * @param {Array} data.rooms - Associated rooms
   * @param {Array<string>} data.tags - Zone tags
   */
  constructor(data) {
    this.id = data.id
    this.name = data.name
    this.loweredName = data.name.toLowerCase()
    this.buildingId = data.building_id
    this.parentId = data.parent_id
    this.type = data.zone_type
    this.capacity = data.capacity
    this.squareFootage = data.square_footage
    this.floors = data.floors
    this.hasGeojson = data.has_geojson
    this.isRoot = data.is_root
    this.layout = data.layout ? ZoneLayout.fromJson(data.layout) : undefined
    this.primaryUsageId = data.primary_usage_id
    this.secondaryUsageId = data.secondary_usage_id
    this.rooms = data.rooms
    this.tags = data.tags
  }

  /**
   * Creates a Zone instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {Zone} New Zone instance
   */
  static fromJson(json) {
    return new Zone(json)
  }
}

/**
 * Represents GeoJSON geometry data.
 *
 * @property {string} type - Geometry type (e.g., "Polygon")
 * @property {Array} coordinates - Coordinate arrays
 */
export class FeatureGeometry {
  /**
   * @param {Object} data - The geometry data
   * @param {string} data.type - Geometry type (e.g., "Polygon")
   * @param {Array} data.coordinates - Coordinate arrays
   */
  constructor(data) {
    this.type = data.type
    this.coordinates = data.coordinates
  }

  /**
   * Creates a FeatureGeometry instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {FeatureGeometry} New FeatureGeometry instance
   */
  static fromJson(json) {
    return new FeatureGeometry(json)
  }
}

/**
 * Represents GeoJSON feature properties.
 *
 * @property {string} id - Feature ID
 * @property {string} name - Feature name
 * @property {string} buildingId - Associated building ID
 * @property {string|null} parentId - Parent feature ID
 * @property {string} subType - Feature sub-type
 * @property {number} capacity - Feature capacity
 * @property {number} sqft - Square footage
 * @property {number} numberOfFloors - Number of floors
 * @property {Array<string>} floors - Floor identifiers
 * @property {string} iconDefaultColor - Default icon color
 * @property {string|null} primaryUsageId - Primary usage type ID
 * @property {string|null} secondaryUsageId - Secondary usage type ID
 * @property {Array<string>} tags - Feature tags
 */
export class FeatureProperties {
  /**
   * @param {Object} data - The properties data
   * @param {string} data.id - Feature ID
   * @param {string} data.name - Feature name
   * @param {string} data.building_id - Associated building ID
   * @param {string|null} data.parent_id - Parent feature ID
   * @param {string} data.sub_type - Feature sub-type
   * @param {number} data.capacity - Feature capacity
   * @param {number} data.sqft - Square footage
   * @param {number} data.number_of_floors - Number of floors
   * @param {Array<string>} data.floors - Floor identifiers
   * @param {string} data.icon_default_color - Default icon color
   * @param {string|null} data.primary_usage_id - Primary usage type ID
   * @param {string|null} data.secondary_usage_id - Secondary usage type ID
   * @param {Array<string>} data.tags - Feature tags
   */
  constructor(data) {
    this.id = data.id
    this.name = data.name
    this.buildingId = data.building_id
    this.parentId = data.parent_id
    this.subType = data.sub_type
    this.capacity = data.capacity
    this.sqft = data.sqft
    this.numberOfFloors = data.number_of_floors
    this.floors = data.floors
    this.floor = data.floors[0]
    this.iconDefaultColor = data.icon_default_color
    this.primaryUsageId = data.primary_usage_id
    this.secondaryUsageId = data.secondary_usage_id
    this.tags = data.tags
  }

  /**
   * Creates a FeatureProperties instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {FeatureProperties} New FeatureProperties instance
   */
  static fromJson(json) {
    return new FeatureProperties(json)
  }
}

/**
 * Represents a GeoJSON feature with geometry and properties.
 *
 * @property {string} type - Feature type ("Feature")
 * @property {FeatureGeometry|undefined} geometry - Geometry data
 * @property {FeatureProperties|undefined} properties - Properties data
 */
export class GeoJsonFeature {
  /**
   * @param {Object} data - The feature data
   * @param {string} data.type - Feature type ("Feature")
   * @param {Object} data.geometry - Geometry data
   * @param {Object} data.properties - Properties data
   */
  constructor(data) {
    this.type = data.type
    this.geometry = data.geometry ? FeatureGeometry.fromJson(data.geometry) : undefined
    this.properties = data.properties ? FeatureProperties.fromJson(data.properties) : undefined
  }

  /**
   * Creates a GeoJsonFeature instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {GeoJsonFeature} New GeoJsonFeature instance
   */
  static fromJson(json) {
    return new GeoJsonFeature(json)
  }
}

/**
 * Represents a GeoJSON feature collection.
 *
 * @property {string} type - Collection type ("FeatureCollection")
 * @property {Array<GeoJsonFeature>} features - Array of features
 */
export class GeoJson {
  /**
   * @param {Object} data - The GeoJSON data
   * @param {string} data.type - Collection type ("FeatureCollection")
   * @param {Array} data.features - Array of features
   */
  constructor(data) {
    this.type = data.type
    this.features = data.features?.map(feature => GeoJsonFeature.fromJson(feature))
  }

  /**
   * Creates a GeoJson instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {GeoJson} New GeoJson instance
   */
  static fromJson(json) {
    return new GeoJson(json)
  }
}

// Zone type hierarchy from most granular (smallest) to least granular (largest)
const ZONE_TYPE_HIERARCHY = [
  ZoneType.INDIVIDUAL, // Highest priority (smallest/most granular)
  ZoneType.DEFAULT,
  ZoneType.FLOOR,
  ZoneType.BUILDING,
  ZoneType.CAMPUS, // Lowest priority (largest/most aggregate)
]

// Helper function to get the smallest type from a list of types based on hierarchy
const getSmallestType = typesList => {
  for (const hierarchyType of ZONE_TYPE_HIERARCHY) {
    if (typesList.includes(hierarchyType)) {
      return hierarchyType
    }
  }
  return null
}

/**
 * Main campus data container with buildings, zones, and geographic information.
 *
 * @property {Object} bbox - Bounding boxes keyed by UUID
 * @property {Array<Building>} buildings - Array of building data
 * @property {Object} buildingLookup - Building lookup table by ID
 * @property {Array<string>} floors - Available floor identifiers
 * @property {GeoJson|undefined} geojson - GeoJSON feature collection
 * @property {boolean} hasCampuses - Whether multiple campuses exist
 * @property {Array} initialBounds - Initial map bounds
 * @property {string} initialCampusId - Default campus ID
 * @property {number} ts - Timestamp
 * @property {Array<string>} types - Available zone types
 * @property {Array<Usage>} usages - Available usage types
 * @property {Object} usageLookup - Usage lookup table by ID
 * @property {Array<Zone>} zones - Zone data
 * @property {Object} zoneLookup - Zone lookup table by ID
 */
export class CampusData {
  /**
   * @param {Object} data - The campus data
   * @param {Object} data.bbox - Bounding boxes keyed by UUID
   * @param {Array} data.buildings - Array of building data
   * @param {Array<string>} data.floors - Available floor identifiers
   * @param {Object} data.geojson - GeoJSON feature collection
   * @param {boolean} data.has_campuses - Whether multiple campuses exist
   * @param {Array} data.initial_bounds - Initial map bounds
   * @param {string} data.initial_campus_id - Default campus ID
   * @param {number} data.ts - Timestamp
   * @param {Array<string>} data.types - Available zone types
   * @param {Array} data.usages - Available usage types
   * @param {Array} data.zones - Zone data
   */
  constructor(data) {
    this.bbox = data.bbox

    this.buildings = data.buildings?.map(building => Building.fromJson(building)) ?? []
    this.buildingLookup = buildIdLookup(this.buildings)

    this.floors = data.floors ?? []
    this.geojson = data.geojson ? GeoJson.fromJson(data.geojson) : undefined
    this.geojsonFeatureLookup = {}
    this.buildingGeoJsonFeatureLookup = {}
    this.floorGeoJsonFeatureLookup = {}
    this.defaultGeoJsonFeatureLookup = {}

    if (!isEmptyArray(this.geojson?.features)) {
      this.geojson.features.forEach(feature => {
        this.geojsonFeatureLookup[feature.properties.id] = feature

        if (
          feature.properties.subType === ZoneType.CAMPUS ||
          feature.properties.subType === ZoneType.SCHOOL
        ) {
          return
        }

        const buildingId = feature.properties.buildingId

        if (feature.properties.subType === ZoneType.BUILDING) {
          this.buildingGeoJsonFeatureLookup[buildingId] = feature
          return
        }

        if (feature.properties.subType === ZoneType.FLOOR) {
          this.floorGeoJsonFeatureLookup[buildingId] ??= []
          this.floorGeoJsonFeatureLookup[buildingId].push(feature)
          return
        }

        const floor = feature.properties.floor

        this.defaultGeoJsonFeatureLookup[buildingId] ??= {}
        this.defaultGeoJsonFeatureLookup[buildingId][floor] ??= []
        this.defaultGeoJsonFeatureLookup[buildingId][floor].push(feature)
      })
    }

    this.hasCampuses = data.has_campuses
    this.initialBounds = data.initial_bounds
    this.initialCampusId = data.initial_campus_id
    this.ts = data.ts
    this.types = data.types ?? []

    this.usages = data.usages?.map(usage => Usage.fromJson(usage)) ?? []
    this.usageLookup = buildIdLookup(this.usages)

    this.zones = []
    this.rootZones = []
    this.buildingZones = []
    this.floorZones = []
    this.zoneIdsByFloorLookup = {}
    this.zoneIdsByBuildingLookup = {}
    this.zoneIdsByUsageLookup = {}
    this.smallestZoneIds = new Set()
    this.parentZoneIdLookup = {}
    this.childZoneIdLookup = {}

    const smallestType = getSmallestType(this.types)

    for (const zone of data.zones ?? []) {
      const newZone = Zone.fromJson(zone)
      this.zones.push(newZone)

      if (newZone.isRoot) {
        this.rootZones.push(newZone)
      }

      if (newZone.type === ZoneType.BUILDING) {
        this.buildingZones.push(newZone)
      }

      if (newZone.type === ZoneType.FLOOR) {
        this.floorZones.push(newZone)
      }

      if (newZone.floors?.length) {
        for (const floor of newZone.floors) {
          this.zoneIdsByFloorLookup[floor] ??= new Set()
          this.zoneIdsByFloorLookup[floor].add(newZone.id)
        }
      }

      if (newZone.buildingId) {
        this.zoneIdsByBuildingLookup[newZone.buildingId] ??= new Set()
        this.zoneIdsByBuildingLookup[newZone.buildingId].add(newZone.id)
      }

      if (newZone.primaryUsageId) {
        this.zoneIdsByUsageLookup[newZone.primaryUsageId] ??= new Set()
        this.zoneIdsByUsageLookup[newZone.primaryUsageId].add(newZone.id)
      }

      if (newZone.secondaryUsageId) {
        this.zoneIdsByUsageLookup[newZone.secondaryUsageId] ??= new Set()
        this.zoneIdsByUsageLookup[newZone.secondaryUsageId].add(newZone.id)
      }

      if (newZone.type === smallestType) {
        this.smallestZoneIds.add(newZone.id)
      }

      if (newZone.parentId) {
        this.childZoneIdLookup[newZone.parentId] ??= new Set()
        this.childZoneIdLookup[newZone.parentId].add(newZone.id)
      }
    }

    const zoneSort = (z1, z2) => {
      const c1 = this.childZoneIdLookup[z1.id]?.size ?? 0
      const c2 = this.childZoneIdLookup[z2.id]?.size ?? 0

      if (c1 < c2) {
        return 1
      } else if (c1 > c2) {
        return -1
      }

      if (z1.name < z2.name) {
        return -1
      } else if (z1.name > z2.name) {
        return 1
      }

      return 0
    }
    this.rootZones = this.rootZones.sort(zoneSort)

    this.zoneLookup = buildIdLookup(this.zones)
    this.hasZones = this.zones.length > 0
  }

  /**
   * Creates a CampusData instance from JSON data.
   * @param {Object} json - JSON data
   * @returns {CampusData} New CampusData instance
   */
  static fromJson(json) {
    return new CampusData(json)
  }

  static fromRecord(record) {
    const data = new CampusData({})
    for (const [key, value] of Object.entries(record)) {
      data[key] = value
    }

    return data
  }

  /**
   * Finds a building by its ID.
   * @param {string} id - Building ID
   * @returns {Building|undefined} Found building or undefined
   */
  getBuildingById(id) {
    return this.buildingLookup[id]
  }

  /**
   * Finds a zone by its ID.
   * @param {string} id - Zone ID
   * @returns {Zone|undefined} Found zone or undefined
   */
  getZoneById(id) {
    return this.zoneLookup[id]
  }

  /**
   * Finds a zone geojson feature by its ID.
   * @param {string} id - Zone ID
   * @returns {GeoJsonFeature|undefined} Found zone feature or undefined
   */
  getZoneFeatureById(id) {
    return this.geojsonFeatureLookup[id]
  }

  getBuildZoneFeatureByBuildingId(buildingId) {
    return this.buildingGeoJsonFeatureLookup[buildingId]
  }

  getBuildingFloorZoneFeaturesByBuildingId(buildingId) {
    return this.floorGeoJsonFeatureLookup[buildingId]
  }

  getDefaultZoneFeaturesByBuildingFloor(buildingId, floor) {
    return this.defaultGeoJsonFeatureLookup[buildingId]?.[floor]
  }

  /**
   * Finds a usage type by its ID.
   * @param {string} id - Usage ID
   * @returns {Usage|undefined} Found usage or undefined
   */
  getUsageById(id) {
    return this.usageLookup[id]
  }

  /**
   * Gets bounding box coordinates for a given ID.
   * @param {string} id - Bounding box ID
   * @returns {Array|null|undefined} Coordinate array, null, or undefined
   */
  getBoundingBox(id) {
    return this.bbox?.[id]
  }

  /**
   * Filters zones by type.
   * @param {string} zoneType - Zone type to filter by
   * @returns {Array<Zone>} Filtered zones
   */
  getZonesByType(zoneType) {
    return this.zones?.filter(zone => zone.type === zoneType) ?? []
  }

  /**
   * Filters zones by building ID.
   * @param {string} buildingId - Building ID to filter by
   * @returns {Array<Zone>} Filtered zones
   */
  getZonesByBuilding(buildingId) {
    if (!this.hasZones) {
      return []
    }

    const zoneIds = this.zoneIdsByBuildingLookup[buildingId]
    if (!zoneIds?.size) {
      return []
    }

    return Array.from(zoneIds)
      .map(zoneId => this.zoneLookup[zoneId])
      .filter(Boolean)
  }

  getZoneIdsByUsageId(usageId) {
    const zoneIds = this.zoneIdsByUsageLookup[usageId]
    if (zoneIds == null) {
      return new Set()
    }

    return zoneIds
  }

  getZoneIdsByBuildingId(buildingId) {
    const zoneIds = this.zoneIdsByBuildingLookup[buildingId]
    if (zoneIds == null) {
      return new Set()
    }

    return zoneIds
  }

  getZoneIdsByFloor(floor) {
    const zoneIds = this.zoneIdsByFloorLookup[floor]
    if (zoneIds == null) {
      return new Set()
    }

    return zoneIds
  }

  /**
   * Zone hierarchies by traversing parent relationships.
   * Returns all zone IDs from the given zone up to the root (school level).
   *
   * @param {string} zoneId - Starting zone ID
   * @returns {Set<string>} Set of parent zone IDs
   */
  getZoneParentIds(zoneId) {
    const cache = this.parentZoneIdLookup[zoneId]
    if (cache != null) {
      return cache
    }

    const out = new Set()
    let current = this.zoneLookup[zoneId]

    while (current) {
      if (current.type !== ZoneType.SCHOOL) {
        out.add(current.id)
      }
      current = this.zoneLookup[current.parentId]
    }

    this.parentZoneIdLookup[zoneId] = out
    return out
  }

  getZoneHierarchy() {
    if (!this.rootZones?.length) {
      return null
    }

    const this2 = this
    const buildZoneData = zone => ({
      id: zone.id,
      name: zone.name,
      description: zone.description,
      type: zone.type,
      buildingId: zone.buildingId,
      iconColor: zone.layout.iconColor,
      isRoot: zone.isRoot,
      hasGeojson: zone.hasGeojson,

      isComplete: () => true,
      hasSubZones: () => {
        return this2.childZoneIdLookup[zone.id]?.size > 0
      },
      getSubZones: () => {
        if (!this2.childZoneIdLookup[zone.id]?.size) {
          return []
        }

        return Array.from(this2.childZoneIdLookup[zone.id]).map(zoneId =>
          buildZoneData(this2.getZoneById(zoneId))
        )
      },
    })

    return this.rootZones.map(buildZoneData)
  }
}
